<template>
  <div class="dash" :class="{pdf: hideNavType == 'pdf'}">
    <!-- multiple: without it, portal-vue only ever renders the LAST <portal to="dash"> to
         register (see PortalTarget's ownTransports) - every other one is silently unmounted
         (destroyed) the moment a new one appears anywhere in the app, even completely unrelated
         ones. DropFile.vue registers here unconditionally on every mount (not gated by v-if,
         only its own content is), and it remounts fresh every time Form.vue does (e.g. switching
         standard/personalization mode) - which was silently destroying an in-progress <MediaEditor>
         export's portal (also targeting "dash") every time, since $destroy() clears all of a
         component's listeners, so its later 'finished'/'export-progress' emits went nowhere. -->
    <portal-target name="dash" multiple/>
    <!-- MediaEditor.vue gets its OWN dedicated target, separate from "dash" - even with `multiple`
         and a stable vnode `key` on <MediaEditor>, DropFile.vue remounting fresh on every Form.vue
         recreation (see above) was still occasionally causing portal-vue's shared PortalTarget to
         recreate the MediaEditor component instance instead of patching it in place (confirmed via
         instrumentation: same Wormhole transport entry, but a new Vue component _uid) - a brief
         flash of the popup reopening/closing mid-export. Giving it a target nothing else ever
         touches removes any reactive dependency on sibling portal churn entirely. -->
    <portal-target name="media-editor-dash" multiple/>
    <componentTootip/>
    <componentContextMenu/>
    <componentModal/>
    <requestManager/>
    <selectUsers/>
    <tableOverlay/>
    <TourOverlay/>


    <div class="navigator-unmanaged" v-if="navigatorUnmanaged && !useNavigatorUnmanaged">
      <div>
        <div class="title">{{$t('dash.navigator-unmanaged.title')}}</div>
        <div class="content" v-html="$t('dash.navigator-unmanaged.content')"></div>
        <div class="content-use" @click="useNavigator()">{{ $t('dash.navigator-unmanaged.content-use') }}</div>
      </div>
    </div>

    <div class="dash-section" :class="{blur: navigatorUnmanaged && !useNavigatorUnmanaged}">
      <component-tabs :section="section" :scrolled="this.fixed" :userProp="this.user" v-on:tab-clicked="update"
            @disclaimer-post="disclaimerPost" :checkDisclaimerPost="checkDisclaimerPost"
            @initTabsMinimize="initTabsMinimize"></component-tabs>

      <div class="main-wrapper" :class="[{'screenshot-preview': screenshotPuppeteer}]">
        <SelectPageSection
          v-show="['postCreator', 'posts2', 'boosting'].includes($route.name) || isPMRoute || ($route.name == 'reporting' && isReportingV2)"
          :title-key="
            $route.name === 'reporting'
            ? 'dash.select_page_section.reporting'
            : $route.name === 'boosting'
            ? 'dash.select_page_section.boosting'
            : (isPMRoute
              ? 'dash.tab.presenceManagement'
              : 'dash.select_page_section.publications')
          "
          :filter-networks="
            $route.name === 'reporting'
              ? ['facebook', 'instagram', 'linkedin', 'tiktok']
              : $route.name === 'boosting'
              ? ['facebook']
              : (isPMRoute ? pmAllowedNetworks : [])
          "
          :filter-fn="$route.name === 'reporting' ? reportingPageFilter : $route.name === 'boosting' ? boostingPageFilter : (isPMRoute ? pmPageFilter : null)"
        />
        <div class="main-content">
          <!-- Header carries its own banner; routes without header need a standalone one -->
          <NewVersionBanner v-if="['postCreator','plan'].includes($route.name)"/>
          <component-header :scrolled="this.scrolled" :userProp="this.user" :section="section" v-on:dropdown-clicked="update"
          v-on:search="search" v-on:clickAddPost="addPostHeader = true" v-if="!['postCreator','plan'].includes($route.name)"></component-header>
     

          <!-- <div class="section" :class="{'section-fixed' : scrolled == true, 'large': ['overview', 'pages', 'bots', 'sources', 'settings', 'invoice'].includes(section)}"> -->
          <div class="section large" :class="{ 'section-fixed': scrolled == true, 'accordeon': !tabsIsMinimize && !(['overview', 'customDashboard', 'pages', 'bots', 'sources', 'settings', 'invoice', 'plan', 'boosting'].includes(this.$route.name) || ($route.name == 'reporting' && isReportingV2)) , [this.$route.name] : true}">
            <router-view @on-settings-changed="settings" @on-show-onboarding="onboard" :addBot="addBot"
              @on-close-bot-form="addBot = false" :addPostHeader="addPostHeader"
              @on-close-post-form="addPostHeader = false" @on-open-post-form="addPostHeader = true" @disclaimer-post="disclaimerPost"
              :tabsIsMinimize="tabsIsMinimize" @minimizeTabs="minimizeTabs" @hideNav="hideNav"/>
              <!-- <ModalSwitchVersion/> -->
            <!--<div class="footer">
              <p>Copyright {{getYear()}} - Mashup Web</p>
            </div>-->
          </div>
        </div>
      </div>
    </div>

    <!-- Dialog (shared among pages) -->
    <vs-popup :title="dialog && dialog.title" :active.sync="dialog && dialog.show" class="overlay onboarding">
      <!-- Advice: free user -->
      <vs-row v-if="userPlan == '' && user && user.partner">
        <vs-col vs-type="flex" vs-align="center" class="flex-d-c col-left">
          <p>{{ dialog.message }}</p>
          <img v-if="dialog.gif == 'page'" src="../assets/img/onboarding/connectpage.gif" />
          <img v-if="dialog.gif == 'post'" src="../assets/img/onboarding/post.gif" />
          <img v-if="dialog.gif == 'bot'" src="../assets/img/onboarding/bot.gif" />
          <vs-button
            @click="dialog.show = false; dialog.gif == 'bot' ? addBot = true : update(1, `${dialog.gif}s`, { showDialog: true })"
            class="btn-img" color="#b44884" vs-type="filled">
            <p>{{ dialog.cta }}</p>
          </vs-button>
        </vs-col>
      </vs-row>
      <!-- Advice: paid user -->
      <vs-row v-else>
        <vs-col v-if="dialog.gif == 'page' || dialog.gif == 'post'" vs-type="flex" vs-align="center"
          class="flex-d-c col-left">
          <p>{{ dialog.message }}</p>
          <img v-if="dialog.gif == 'page'" src="../assets/img/onboarding/connectpage.gif" />
          <img v-if="dialog.gif == 'post'" src="../assets/img/onboarding/post.gif" />
          <p></p>
          <vs-button @click="dialog.show = false;" class="btn-img" color="#b44884" vs-type="filled">
            <p>{{ dialog.cta }}</p>
          </vs-button>
        </vs-col>
        <template v-else-if="dialog.gif === 'bot'">
          <vs-col vs-type="flex" vs-align="center" vs-xs="12" vs-sm="6" vs-lg="6" class="flex-d-c col-left">
            <p>{{ dialog.message }}</p>
            <img src="../assets/img/onboarding/bot.gif" />
            <p></p>
            <vs-button @click="dialog.show = false; addBot = true" class="btn-img" color="#b44884" vs-type="filled">
              <p>{{ dialog.cta }}</p>
            </vs-button>
          </vs-col>
          <vs-col vs-type="flex" vs-align="center" vs-xs="12" vs-sm="6" vs-lg="6" class="flex-d-c col-right">
            <p>{{ dialog.message2 }}</p>
            <img src="../assets/img/onboarding/sources.gif" />
            <p></p>
            <vs-button @click="dialog.show = false; update(1, 'sources', { showDialog: true })" class="btn-img"
              color="#b44884" vs-type="filled">
              <p>{{ dialog.cta2 }}</p>
            </vs-button>
          </vs-col>
        </template>
      </vs-row>
    </vs-popup>
  </div>
</template>

<script>
import store from './../store/index';
import moment from 'moment'

import componentHeader from './../components/dash/Header.vue'
import componentTabs from './../components/dash/Tabs.vue'
import componentTootip from '../components/dash/tooltip/tooltip.vue'
import componentContextMenu from '../components/dash/contextMenu/contextMenu.vue'
import componentModal from '../components/dash/modalPopup.vue'
import SelectPageSection from '../components/dash/SelectPageSection.vue'
import { isBoostingPage } from '@/utils/boosting'

import requestManager from '../components/dash/RequestManager.vue'
import selectUsers from '../components/dash/overlay/SelectUsers.vue'
import tableOverlay from '../components/dash/overlay/Table.vue'
import ModalSwitchVersion from '@/components/dash/posts/components/ModalSwitchVersion.vue'
import TourOverlay from '@/components/tours/TourOverlay.vue'
import NewVersionBanner from '@/components/dash/NewVersionBanner.vue'

import Pages from '../components/mixins/Pages'
import Groups from '../components/mixins/Groups'
import Bots from '../components/mixins/Bots'
import User from '../components/mixins/User'
import Notif from '../components/mixins/Notif'

import { EventBus } from '@/event-bus';

export default {
  name: 'dash',

  components: {
    componentHeader,
    NewVersionBanner,
    componentTabs,
    componentTootip,
    componentContextMenu,
    componentModal,
    SelectPageSection,
    requestManager,
    selectUsers,
    tableOverlay,
    ModalSwitchVersion,
    TourOverlay
  },

  mixins: [Pages, Groups, Bots, User, Notif],

  data() {
    return {
      scrolled: false,
      fixed: false,

      get section() {
        let section = localStorage.getItem('section')
        return section
      },

      get pages() {
        if (localStorage.getItem('pages')) {
          return JSON.parse(localStorage.pages).length > 0
        } else {
          return false
        }
      },

      get bots() {
        if (localStorage.getItem('bots')) {
          return JSON.parse(localStorage.bots).length > 0
        } else {
          return false
        }
      },

      window: {
        width: 0
      },
      user: {},
      userPlan: "",
      enable: {
        overview: false,
        bots: false,
        posts: false,
        planner: false,
        moderation: false,
        sources: false
      },
      dialog: {
        show: false,
        title: '',
        // alternativeTitle:this.$t('onboarding.alternative_ttl'),
        message: null
      },
      addBot: false,
      addPostHeader: false,
      checkDisclaimerPost: false,
      tabsIsMinimize: false,
      hideNavType: null,
      useNavigatorUnmanaged: localStorage.useNavigatorUnmanaged ? localStorage.useNavigatorUnmanaged : false
    }
  },

  computed: {
    isPMRoute() {
      return !!(this.$route && this.$route.name && String(this.$route.name).startsWith('presence-management'))
    },

    isTesterUserInProd() {
      if (process.env.NODE_ENV !== 'production') return false
      const u = this.user || (localStorage.user && localStorage.user !== 'undefined' ? JSON.parse(localStorage.user) : null)
      if (!u) return false
      const isMashup = u.email && u.email.toLowerCase().endsWith('@mashup-web.com')
      if (isMashup) return false
      const testerIds = ['9e046e84-5b26-4c94-8379-9a88ea278490', '6a184790-5f10-43eb-8f16-05daa21d6dc5']
      return testerIds.includes(u.account) || testerIds.includes(u.id)
    },

    pmAllowedNetworks() {
      return this.isTesterUserInProd ? ['google', 'facebook'] : ['google', 'facebook', 'apple']
    },

    pmPageFilter() {
      return function(page) {
        if (page.story_config && page.story_config.onlyConciergerie) return false
        return true
      }
    },

    boostingPageFilter() {
      return isBoostingPage
    },

    reportingPageFilter() {
      return function(page) {
        if (page.story_config && page.story_config.onlyConciergerie) return false
        if (page.isGroup) return false
        if (page.network === 'linkedin' && !/^\d+$/.test(page.network_id)) return false
        return true
      }
    },

    isReportingV2() {
      const envVersion = process.env.VUE_APP_REPORTING_VERSION || '1'
      if (envVersion === '1') return false
      const stored = localStorage.getItem('reportingVersionV2')
      return stored !== null ? stored !== '1' : true
    },

    navigatorUnmanaged() {
      
      // const user = this.user ? this.user
      //   : (localStorage.user && localStorage.user != 'undefined' ? JSON.parse(localStorage.user) : null)
      // let authorizedAccounts = [
      //   'd12717da-7255-4d0d-a77c-417578e016a8', //sandbox
      //   '0f41e9c0-d4d6-42cc-9373-ec0fec0c2fa2', //coucoocabanes
      // ]
      // if(user) return !authorizedAccounts.includes(user.account)

      const userAgent = window.navigator.userAgent;
      const isChromium = (window.chrome !== null && typeof window.chrome !== 'undefined');

      if ((userAgent.includes("Edg") && isChromium) // Edge Chromium
            || userAgent.includes("Edge") // Edge (Legacy/IE Mode)
            || (userAgent.includes("Trident") && userAgent.includes("rv:11.0"))) //"IE 11"
        return true

      return false
    },

    getEnv() {
      return process.env.VUE_APP_API_HOST
    },

    getUserRole() {
      // const user = localStorage.user && localStorage.user != 'undefined' ? JSON.parse(localStorage.user) : null
      const user = this.user ? this.user
        : (localStorage.user && localStorage.user != 'undefined' ? JSON.parse(localStorage.user) : null)
      if (!user) return null
      if (user.admin)
        return 'Admin'
      else
        return user.role && user.role.length
          ? user.role[0]
          : 'Editor'

    },

    screenshotPuppeteer() {
      return !!(localStorage && localStorage.preview_overlay && localStorage.screenshot);
    }
  },

  methods: {

    useNavigator() {
      this.useNavigatorUnmanaged = true
      localStorage.useNavigatorUnmanaged = true
    },

    hideNav(type) {
      console.log('hideNav', type)
      this.hideNavType = type
    },

    minimizeTabs(val) {
      this.tabsIsMinimize = val
    },

    disclaimerPost(val) {
      this.checkDisclaimerPost = val
    },

    notifyTrial() {
      //notify each 12 hours
      if(!localStorage.notifyTrial)
        localStorage.setItem('notifyTrial', Date.now())
      else if(parseInt(localStorage.notifyTrial) + 48*60*60*1000 <= Date.now()) {
        localStorage.setItem('notifyTrial', Date.now())
        this.$vs.notify({
          title: this.$t('dash.trial.title', { count: this.daysRemainingTrial() }),
          text: this.$t('dash.trial.body', { count: this.daysRemainingTrial() }),
          color: '#842993',
          icon: 'timer',
          fixed: true,
        })
      }

    },
    notifyTrialMaybe() {
      if (!this.user || !this.isUserOnTrial) return
      this.notifyTrial()
      this.userPlan = "trial"
    },
    userPremiumMaybe() {
      if (!this.user || !this.isUserPremiumActive) return
      this.userPlan = "premium"
    },
    getYear() {
      return moment().format('YYYY')
    },
    /**
     * Handle window scroll to fix items pos on mobile
     */
    scroll() {
      if (this.window.width < 1024) {
        this.scrolled = window.scrollY >= 70
        this.fixed = window.scrollY >= 70
      } else {
        this.scrolled = false
        this.fixed = window.scrollY >= 80
      }
    },
    /**
     * Handle window resize to fix items pos on mobile
     */
    resize() {
      if (!this.window) return
      this.window.width = window.innerWidth
    },

    initTabsMinimize() {
      this.tabsIsMinimize = false
    },

    /**
     * Update content on tab click
     */
    update(index, section, params = {}) {
      console.log('tabs', index, section, this.section)
      if (section === this.section && index === 1) {
        if(section == 'pages' && (!localStorage.pages || !localStorage.pages.length)) {
          this.dialog.title = this.$t('onboarding.page_ttl')
          this.dialog.message = this.$t('onboarding.page_description')
          this.dialog.cta = this.$t('onboarding.page_cta')
          this.dialog.gif = 'page'
          this.dialog.show = true
        }
        return false
      }

      //if(localStorage.section == 'overview'){
      if (index !== 1) {
        localStorage.setItem('section', 'settings')
        location.href = '/#/dash/settings'
        if (section === 'logout') {
          // Log out
          window.$crisp.push(["do", "session:reset"])
          localStorage.clear()
          location.href = '/'
        } else {
          if (this.getUserRole && this.getUserRole != 'Viewer' && (!localStorage.pages || !localStorage.pages.length || ['undefined', '[]'].includes(localStorage.pages))) {
            localStorage.setItem('section', 'pages')
            this.$router.push({
              name: 'pages'
            })
          }
          else {
            // Always enable pages section
            console.log('section', section)
            localStorage.setItem('section', section)
            location.href = `/#/dash/${section}`
          }
        }
      } else {
        // Always enable pages section
        console.log('section', section)
        localStorage.setItem('section', section)
        location.href = `/#/dash/${section}`
      }
      /*}
      else {
        if (index !== 1) {
          // Enable all settings sections
          localStorage.setItem('section', 'settings')
          if (section === 'logout') {
            // Log out
            localStorage.clear()
            this.$router.push({
              name: 'auth',
              params
            })
          } else {
            // Settings
            this.$router.push({
              name: section,
              params
            })
          }
        } else {
          // if (section !== 'pages' && section !== 'sources' && section !== 'reporting') {
          //   this.setEnable()
          //   // Onboarding rules
          //   console.log("this.enable", this.enable)
          //   if (this.enable[section]) {
          //     //if(section == 'bots') this.addBot = true
          //     localStorage.setItem('section', section)
          //     this.$router.push({
          //       name: section,
          //       params
          //     })
          //   } else {
          //     if (!this.enable.bots) {
          //       this.dialog.title = this.$t('onboarding.page_ttl')
          //       this.dialog.message = this.$t('onboarding.page_description')
          //       this.dialog.cta = this.$t('onboarding.page_cta')
          //       this.dialog.gif = 'page'
          //     } else {
          //       if(this.userPlan == ""){
          //         this.dialog.title = this.$t('onboarding.bot_ttl')
          //       } else {
          //         this.dialog.title = this.$t('onboarding.alternative_ttl')
          //       }
          //       this.dialog.message = this.$t('onboarding.bot_description')
          //       this.dialog.message2 = this.$t('onboarding.sources_description')
          //       this.dialog.cta = this.$t('onboarding.bot_cta')
          //       this.dialog.cta2 = this.$t('onboarding.sources_cta')
          //       this.dialog.gif = 'bot'
          //     }
          //     this.dialog.show = true
          //   }
          // } else {
            console.log('section', section)
            // Always enable pages section
            localStorage.setItem('section', section)
            this.$router.push({
              name: section,
              params
            })
          // }
        }
      }*/

    },

    onboard(step) {
      if (!step || step === 0) {
        this.dialog.title = this.$t('onboarding.page_ttl')
        this.dialog.message = this.$t('onboarding.page_description')
        this.dialog.cta = this.$t('onboarding.page_cta')
        this.dialog.gif = 'page'
      } else if (step === 1) {
        if (this.userPlan == "" && this.user && this.user.partner) {
          this.dialog.title = this.$t('onboarding.bot_ttl')
        } else {
          this.dialog.title = this.$t('onboarding.alternative_ttl')
        }
        this.dialog.message = this.$t('onboarding.bot_description')
        this.dialog.message2 = this.$t('onboarding.sources_description')
        this.dialog.cta = this.$t('onboarding.bot_cta')
        this.dialog.cta2 = this.$t('onboarding.sources_cta')
        this.dialog.gif = 'bot'
      }
      this.dialog.show = true
    },

    /**
     * Enable users nav
     */
    setEnable() {
      this.enable.overview = this.pages && this.bots
      this.enable.bots = this.pages
      //this.enable.posts = this.bots
      this.enable.posts = this.pages
      this.enable.planner = this.pages
      this.enable.moderation = this.pages
      this.enable.sources = this.bots
    },

    /**
     * Update user
     */
    settings(user) {
      this.user = user
    },

    async getBots(noRedirect = false) {
      const routeAtCallTime = this.$route.name

      if (!localStorage.bots) {
        await this.getAllBots({}).then(res => {
          const { success, bots } = res.body
          if (!success || success !== true) {
            this.dialog.title = 'Error'
            if (!res.body) {
              this.dialog.message = 'Server not responding…'
            } else {
              this.dialog.message = res.body.statusText
            }
            this.dialog.show = true
            return
          }

          localStorage.setItem('bots', JSON.stringify(bots))
          // oauth_flow: mid-flow for the MCP "remote connector" (see router/index.js's /dash
          // beforeEnter) - the user must land on the Clés API popup, not get bounced to
          // 'overview' before they can see/approve it. The payment-required guard is unaffected:
          // it runs in the router's beforeEnter, before this component even mounts.
          if (this.$route.query.twofactor || this.$route.query.oauth_flow || noRedirect) {
            return;
          }
          if (this.$route.name !== routeAtCallTime) return
          if (Array.isArray(bots) && bots.length > 0) {
            this.goToSection('overview')
          } else {
            this.goToSection('bots')
          }

        })
      } else {
        if (this.$route.query.twofactor || this.$route.query.oauth_flow || noRedirect) {
          return
        }
        if (this.$route.name !== routeAtCallTime) return
        let cachedBotsList = []
        try {
          cachedBotsList = localStorage.bots ? JSON.parse(localStorage.bots) : []
        } catch (error) {
          console.error(error)
          localStorage.removeItem('bots')
        }
        if (cachedBotsList.length > 0) {
          if (this.section) {
            //this.goToSection(this.section)
          } else {
            this.goToSection('overview')
          }
        } else {
          this.goToSection(this.section)
        }
      }
    },

    goToSection(section) { 
      if(localStorage.getItem('redirect_params_url_action')) return
      if (this.getUserRole == 'Viewer')
        section = 'planner'

      localStorage.setItem('section', section)
      this.$router.push({
        name: section
      })
    },

    search(search) {
      this.setEnable()
      if (this.enable['posts']) {
        localStorage.setItem('section', 'posts')

        if (!this.$router.currentRoute.path.includes('/posts')) {
          this.$router.push({
            name: 'posts',
            params: {
              post: search
            }
          })
        } else {
          this.$router.currentRoute.params.post = search
        }
      } else {
        this.dialog.title = this.$t('onboarding.bot_ttl')
        this.dialog.message = this.$t('onboarding.bot_description')
        this.dialog.show = true
      }
    },

    refreshUser() {
      console.log("refreshUser")
      this.user = localStorage.user ? JSON.parse(localStorage.user) : this.user 
    }
  },

  async created() {
    this.getFrontVersion().then(res => {
      const { success, front_version } = res.body
      if (!success || success !== true) {
        this.dialog.title = 'Error'
        if (!res.body) {
          this.dialog.message = 'Server not responding…'
        } else {
          this.dialog.message = res.body.statusText
        }
        this.dialog.show = true
        return
      }
      localStorage.setItem('front_version', front_version)
    })
    let isRedirectUrl = localStorage.getItem('redirect_params_url')

    // const allBotsResponse = await this.getAllBots({});
    //   const { success: botsSuccess, bots } = allBotsResponse.body;
    //   if (!botsSuccess) {
    //     this.dialog.title = 'Error';
    //     this.dialog.message = allBotsResponse.body ? allBotsResponse.body.statusText : 'Server not responding…';
    //     this.dialog.show = true;
    //     return;
    //   }
    //   localStorage.setItem('bots', JSON.stringify(bots));

    await this.getPages({}).then(async res => {
      const { success, pages } = res.body
      if (!success || success !== true) {
        this.dialog.title = 'Error'
        if (!res.body) {
          this.dialog.message = 'Server not responding…'
        } else {
          this.dialog.message = res.body.statusText
        }
        this.dialog.show = true
        return
      }

      if (Array.isArray(pages) && pages.length > 0) {
        // If page(s) connected found
        localStorage.setItem('pages', JSON.stringify(pages))

        // Pages exists
        // Find bots
        await this.getBots(isRedirectUrl)
        

        //constuct object pageId(key) pageData(value)
 
        await this.$store.dispatch('selectPage/init');
        this.$store.dispatch('variables/init');

      } else {
        // If no page connected found
        if((this.user && this.user._premium && this.user._premium.needAddPaymentMethod) == false)
        this.goToSection('pages')
      }
    }).catch((error) => {
      console.error(error)
      this.dialog.title = 'Error'
      this.dialog.message = 'Server not responding…'
      this.dialog.show = true
    })

    this.$store.dispatch('users/fetchUsers')
    await this.$store.dispatch('users/fetchCurrentUser')
    
    
  },

  async mounted() {
    this.$once('hook:updated', this.notifyTrialMaybe)

    localStorage.removeItem('synchronizedPosts')
    localStorage.removeItem('postingInProgress')

    // Redirect to Pages section if coming from a callback
    const usp = new URLSearchParams(location.search)
    const uspRedirect = localStorage.getItem('redirect_params_url') ? new URLSearchParams(new URL(localStorage.getItem('redirect_params_url')).search) : null
    localStorage.removeItem('redirect_params_url')
    if(uspRedirect) localStorage.setItem('redirect_params_url_action', true)
    const callbackParams = [
      'code',
      'test_callback',
      'oauth_token'
    ]

    let params
    const currentUrl = location.href

    let user = this.user ? this.user
                             : (localStorage.user && localStorage.user != 'undefined' ? JSON.parse(localStorage.user) : null)
    if(user) {
      window.$crisp.push(["set", "user:email", user.email])
      window.$crisp.push(["set", "user:nickname", [`${user.firstname} ${user.lastname}`]])
      if(user.phone)
        window.$crisp.push(["set", "user:phone", [user.phone]])

      //show crisp only on premium & conciergerie option
      if(!(user && user._premium && (user._premium.plan == 3 || (user._premium.options_plan && user._premium.options_plan.includes('conciergerie'))))){
        document.querySelectorAll(".cc-1kgzy").forEach((element) => element.setAttribute('style', `display:none !important;`));
        document.querySelectorAll(".cc-coh7k").forEach((element) => element.setAttribute('style', `display:none !important;`));
        document.querySelectorAll(".cc-1i61z").forEach((element) => element.setAttribute('style', `display:none !important;`));
      }
    }

    
    if (callbackParams.some(p => (uspRedirect ? uspRedirect : usp).has(p))) {
      this.$router.push('pages')
    }
    else if (['post_id', 'page_id'].every(p => (uspRedirect ? uspRedirect : usp).has(p))) {

      const post_id = (uspRedirect ? uspRedirect : usp).get('post_id')
      const page_id = (uspRedirect ? uspRedirect : usp).get('page_id')
      const bot_id = (uspRedirect ? uspRedirect : usp).get('bot_id')
      params = {
        pageId: page_id,
        botId: bot_id,
        newsId: post_id
      }
      if((uspRedirect ? uspRedirect : usp).has('edit_post'))
        params.editPost = post_id
      else if((uspRedirect ? uspRedirect : usp).has('show_post'))
        params.showPost = post_id
      else if((uspRedirect ? uspRedirect : usp).has('validate_post'))
        params.validatePost = post_id
      else if((uspRedirect ? uspRedirect : usp).has('schedule_post'))
        params.schedulePost = post_id
      if((uspRedirect ? uspRedirect : usp).has('is_draft'))
        params.isDraft = true
      if((uspRedirect ? uspRedirect : usp).has('is_error'))
        params.isError = true 
      if((uspRedirect ? uspRedirect : usp).has('no_redirection'))
        params.no_redirection = true
      history.replaceState({}, document.title, location.pathname)
      
      this.$router.push({
        name: 'planner',
        params
      })
    }
    else if(['moderation', 'object_id'].every(p => (uspRedirect ? uspRedirect : usp).has(p))) {
      // `?moderation=1ac1bbd8-b788-4ab5-8629-fc65ae070290&object_id=18090752857622760`
      params = {
        moderation: (uspRedirect ? uspRedirect : usp).get('moderation'),
        object_id: (uspRedirect ? uspRedirect : usp).get('object_id')
      }
      history.replaceState({}, document.title, location.pathname)
      this.$router.push({
        name: 'moderation',
        params
      })
    }
    else if(this.getUserRole == 'Viewer')
      this.$router.push('planner')

    // if (localStorage.user && localStorage.user != "undefined")
    await this.fetchUserInfo()

    // Safe localStorage access with error handling
    try {
      const sessionStart = localStorage.getItem('session_start');
      if(!sessionStart) {
        localStorage.setItem('session_start', Date.now());
      } else {
        const sessionStartTime = parseInt(sessionStart);
        // Check for valid number before comparison
        if (!isNaN(sessionStartTime) && sessionStartTime + 30*24*60*60*1000 <= Date.now() && !localStorage.getItem('pdf')) {
      const resetSession = () => {
        localStorage.clear()
            window.$crisp.push(["do", "session:reset"])
            if(params)
              localStorage.setItem('redirect_params_url', currentUrl)
            location.href = '/'  
      }
      this.$vs.dialog({
          color: 'danger',
          title: this.$t('dash.notifications.warning_title'),
          text: this.$t('dash.notifications.warning_body_session_expired'),
          type: 'confirm',
          accceptText: "OK",
          accept: () => {
            resetSession()  
          },
          cancelText: "Fermer",
          cancel: () => {
            resetSession()
          }
        }
      )
    }
      }
    } catch (error) {
      // Handle localStorage access error
      console.error('Session check failed:', error);
    }

    // Safe parsing of user data
    try {
      const userData = localStorage.getItem('user');
      this.user = userData ? JSON.parse(userData) : null;
    } catch (error) {
      this.user = null;
    }

    EventBus.$on('handlerRefreshUser', this.refreshUser)

    // Tour prompt after onboarding completion (dev/sandbox only)
    this.$nextTick(() => {
      const isDevelopment = process.env.NODE_ENV !== 'production'
      const pendingTour = localStorage.getItem('tour_prompt_pending')
      if (isDevelopment && pendingTour && !this.$store.getters['tours/isTourCompleted'](pendingTour)) {
        localStorage.removeItem('tour_prompt_pending')
        EventBus.$emit('showModal',
          this.$t('tours.prompt.title'),
          this.$t('tours.prompt.content'),
          this.$t('tours.prompt.yes'),
          this.$t('tours.prompt.no'),
          () => {
            this.$router.push({ name: 'posts2' })
            this.$store.dispatch('tours/startTour', 'planner')
          },
          null
        )
      }
    })
  },

  beforeMount() {
    window.addEventListener('scroll', this.scroll)
    window.addEventListener('resize', this.resize)
    this.resize()
    // document.body.classList.add('dash');
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.scroll)
    window.removeEventListener('resize', this.resize)
    EventBus.$off('handlerRefreshUser', this.refreshUser)
  }
}
</script>

<style>
  div#sd-cmp div>svg {
    display: none;
  }
</style>